'use client'

import * as React from 'react'
import { useState } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const textareaVariants = cva(
  'flex w-full rounded-md border text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-y',
  {
    variants: {
      variant: {
        default: 'border-input bg-background',
        filled: 'border-input bg-surface-hover',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  error?: boolean
  label?: string
  helperText?: string
  errorMessage?: string
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, error, label, helperText, errorMessage, maxLength, onChange, value, defaultValue, ...props }, ref) => {
    const [charCount, setCharCount] = useState(() => {
      const initial = (value ?? defaultValue ?? '') as string
      return initial.length
    })

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length)
      onChange?.(e)
    }

    const textareaElement = (
      <div className={cn('relative', error && 'ring-1 ring-[var(--color-pink)] rounded-md')}>
        <textarea
          className={cn(textareaVariants({ variant }), 'px-3 py-2 min-h-[80px]', className)}
          ref={ref}
          maxLength={maxLength}
          onChange={handleChange}
          value={value}
          defaultValue={defaultValue}
          {...props}
        />
        {maxLength && (
          <span className="absolute bottom-2 right-2 text-xs text-muted-foreground pointer-events-none">
            {charCount}/{maxLength}
          </span>
        )}
      </div>
    )

    if (!label && !helperText && !errorMessage) {
      return textareaElement
    }

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        {textareaElement}
        {error && errorMessage ? (
          <p className="text-xs text-[var(--color-pink)]">{errorMessage}</p>
        ) : helperText ? (
          <p className="text-xs text-muted-foreground">{helperText}</p>
        ) : null}
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'

export { Textarea, textareaVariants }
